import { supabase } from '../../lib/supabase'
import { getAccounts } from './accountStore'
import { ADMINISTRATIVE_ACTIONS, describeAuditEvent, type ActivityKind, type AuditRow } from './auditEvents'

/**
 * What the administrator's Overview reads: counts taken from the accounts,
 * teams, and employees tables, and the latest administrative events from the
 * audit log (an administrator reads all of it, audit_events_read_administrator),
 * described by auditEvents.ts. Nothing here is stored for display; every
 * figure is read when the page opens.
 */
export type { ActivityKind }

export type ActivityItem = {
  id: string
  kind: ActivityKind
  title: string
  detail: string
  occurredAt: string
}

export type AdminOverview = {
  activeAccounts: number
  deactivatedAccounts: number
  openTeams: number
  employees: number
  activity: ActivityItem[]
}

const ACTIVITY_LIMIT = 10

/**
 * "2h ago", "Yesterday", or "Sep 12": how long ago an event happened, the
 * way the Overview lists it.
 */
export function whenLabel(occurredAt: string, now: Date = new Date()): string {
  const then = new Date(occurredAt)
  const minutes = Math.floor((now.getTime() - then.getTime()) / 60_000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (then.toDateString() === now.toDateString()) return `${Math.floor(minutes / 60)}h ago`
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (then.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export async function getAdminOverview(): Promise<AdminOverview> {
  const today = new Date().toISOString().slice(0, 10)
  const [accounts, teamsResult, employeesResult, auditResult] = await Promise.all([
    getAccounts(),
    supabase.from('teams').select('id, name, close_date'),
    supabase.from('employees').select('id, display_name'),
    supabase
      .from('audit_events')
      .select('id, actor, action, subject_id, previous_value, new_value, reason, occurred_at')
      .in('action', ADMINISTRATIVE_ACTIONS)
      .order('occurred_at', { ascending: false })
      .limit(ACTIVITY_LIMIT),
  ])

  const teams = (teamsResult.data ?? []) as { id: string; name: string; close_date: string | null }[]
  const employees = (employeesResult.data ?? []) as { id: string; display_name: string }[]
  const accountById = new Map(accounts.map((account) => [account.id, account]))
  const employeeById = new Map(employees.map((employee) => [employee.id, employee.display_name]))
  const teamById = new Map(teams.map((team) => [team.id, team.name]))

  const lookups = { accounts: accountById, employees: employeeById, teams: teamById }
  const activity: ActivityItem[] = ((auditResult.data ?? []) as unknown as AuditRow[]).map((row) => ({
    id: row.id,
    occurredAt: row.occurred_at,
    ...describeAuditEvent(row, lookups),
  }))

  return {
    activeAccounts: accounts.filter((account) => account.active).length,
    deactivatedAccounts: accounts.filter((account) => !account.active).length,
    openTeams: teams.filter((team) => team.close_date === null || team.close_date >= today).length,
    employees: employees.length,
    activity,
  }
}
